import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import { NARROW_ONLY, WIDE_ONLY_CELL } from "../responsiveTable";
import { Explanation } from "../support/Explanation/Explanation";
import { DocumentsEmptyState } from "./DocumentsEmptyState";
import { DownloadDocumentIconButton } from "./DownloadDocumentIconButton/DownloadDocumentIconButton";
import { PreviewDocumentButton } from "./PreviewDocumentButton";
import { getFileExtension, isPdfExtension } from "./utils";

interface DocumentRow {
  id: string;
  name: string;
  uploadedAt: string;
  fileExtension?: string;
}

interface Props {
  documents: DocumentRow[];
  // Document ids that must download via the sourcing-event-scoped (access-restricted) route rather than the
  // generic one — used for documents the buyer published to all tenderers. Requires eventId.
  restrictedDocumentIds?: Set<string>;
  eventId?: string;
  // Copy for the shared empty state; callers pass context-specific text (title defaults generically).
  emptyTitle?: string;
  emptyDescription?: string;
}

const isPreviewable = (row: DocumentRow) => isPdfExtension(row.fileExtension ?? getFileExtension(row.name));

export const DocumentsAsOwnerTable = ({
  documents,
  restrictedDocumentIds,
  eventId,
  emptyTitle,
  emptyDescription,
}: Props) => {
  const { t } = useTranslation();

  if (documents.length === 0) {
    return <DocumentsEmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <Box borderRadius={"md"} border="1px solid" borderColor="smBorder" pb="2.5">
      <Table variant="simple" sx={{ "th, td": { px: { base: 3, md: 6 } } }}>
        <Thead>
          <Tr>
            <Th>{t("Name")}</Th>
            <Th whiteSpace="nowrap" display={WIDE_ONLY_CELL}>
              {t("Upload date")}
            </Th>
            <Th width="1%"></Th>
          </Tr>
        </Thead>
        <Tbody>
          {documents.map((e) => {
            const restrictedEventId = restrictedDocumentIds?.has(e.id) ? eventId : undefined;
            const uploadedAt = displayDateWithTime(e.uploadedAt, t("at"));
            return (
              <Tr key={e.id}>
                <Td overflowWrap="anywhere">
                  {isPreviewable(e) ? (
                    <PreviewDocumentButton documentId={e.id} documentName={e.name} eventId={restrictedEventId} />
                  ) : (
                    e.name
                  )}
                  <Text display={NARROW_ONLY} fontSize="sm" color="smMuted" mt="1">
                    {uploadedAt}
                  </Text>
                </Td>
                <Td whiteSpace="nowrap" display={WIDE_ONLY_CELL}>
                  {uploadedAt}
                </Td>
                <Td>
                  <Flex justifyContent="end">
                    <Explanation text={t("Download document")} shouldWrapChildren>
                      <DownloadDocumentIconButton documentId={e.id} eventId={restrictedEventId} />
                    </Explanation>
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
